import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Box,
  Circle,
  Loader2,
  Database,
  AlertCircle,
  Plus,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ALL_CLASSES, getGroupsForDomain, getClassesByGroup } from '@/lib/ontology/schema'
import type { EntityGroup } from '@/lib/ontology/schema'
import type { OntologyClass, OntologyDomain } from '@/types/ontology'
import { useConnectionStore } from '@/stores/connectionStore'
import { getInstancesOfClass } from '@/lib/sparql/queries'
import { useAllInstanceCounts, useInstanceCount } from '@/hooks/useInstances'
import { getLocalName } from '@/lib/ontology/namespaces'
import { InstanceDetailPanel } from '@/components/shared/InstanceDetailPanel'
import { CreateInstanceDialog } from '@/components/shared/CreateInstanceDialog'

const DOMAIN_LABELS: Record<OntologyDomain, string> = {
  core: 'Core Domain',
  market: 'Market Domain',
  business: 'Business Domain',
  solution: 'Solution Domain',
}

const DOMAIN_COLORS: Record<OntologyDomain, { bg: string; text: string; border: string }> = {
  core: { bg: '#fdf6e0', text: '#9a7e1a', border: '#d4a020' },
  market: { bg: '#f0f7f2', text: '#456b52', border: '#5c8a6b' },
  business: { bg: '#e8f3fc', text: '#1f689f', border: '#3196e2' },
  solution: { bg: '#fff7e7', text: '#a66d19', border: '#ffc05f' },
}

interface TreeNodeState {
  expanded: Set<string>
  selected: string | null
}

export function DataTreeView() {
  const { isConnected } = useConnectionStore()
  const [treeState, setTreeState] = useState<TreeNodeState>({
    expanded: new Set(),
    selected: null,
  })
  const [createForClass, setCreateForClass] = useState<OntologyClass | null>(null)

  // Get all class URIs for batched count query
  const allClassUris = useMemo(() => ALL_CLASSES.map((c) => c.uri), [])

  // Single batched query for ALL instance counts (replaces ~40 individual queries)
  const { data: instanceCounts = {} } = useAllInstanceCounts(allClassUris, isConnected)

  const toggleExpand = (nodeId: string) => {
    setTreeState((prev) => {
      const newExpanded = new Set(prev.expanded)
      if (newExpanded.has(nodeId)) {
        newExpanded.delete(nodeId)
      } else {
        newExpanded.add(nodeId)
      }
      return { ...prev, expanded: newExpanded }
    })
  }

  const selectNode = (nodeId: string) => {
    setTreeState((prev) => ({ ...prev, selected: nodeId }))
  }

  // Group classes by domain
  const classesByDomain = useMemo(() => {
    const grouped: Record<OntologyDomain, OntologyClass[]> = {
      core: [],
      market: [],
      business: [],
      solution: [],
    }
    ALL_CLASSES.forEach((cls) => {
      grouped[cls.domain].push(cls)
    })
    return grouped
  }, [])

  // Determine what type of node is selected
  const selectedNodeType = useMemo(() => {
    if (!treeState.selected) return null
    if (['core', 'market', 'business', 'solution'].includes(treeState.selected)) {
      return 'domain'
    }
    const isClass = ALL_CLASSES.some((c) => c.uri === treeState.selected)
    if (isClass) return 'class'
    return 'instance'
  }, [treeState.selected])

  if (!isConnected) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card style={{ maxWidth: '400px', textAlign: 'center', padding: '32px' }}>
          <AlertCircle style={{ width: '48px', height: '48px', color: 'var(--color-warning)', margin: '0 auto 16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Not Connected</h3>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
            Connect to Fuseki in Settings to browse the data tree.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', display: 'flex', gap: '24px' }}>
      {/* Tree Panel */}
      <Card style={{ width: '400px', flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <CardHeader style={{ borderBottom: '1px solid var(--color-border)', padding: '16px', flexShrink: 0 }}>
          <CardTitle style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Database style={{ width: '16px', height: '16px' }} />
            Data Explorer
          </CardTitle>
        </CardHeader>
        <CardContent style={{ flex: 1, overflow: 'auto', padding: '12px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {(['core', 'market', 'business', 'solution'] as OntologyDomain[]).map((domain) => (
              <DomainNode
                key={domain}
                domain={domain}
                classes={classesByDomain[domain]}
                expanded={treeState.expanded}
                selected={treeState.selected}
                instanceCounts={instanceCounts}
                onToggle={toggleExpand}
                onSelect={selectNode}
                onRequestCreate={setCreateForClass}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detail Panel */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {treeState.selected ? (
          <DetailPanel
            nodeId={treeState.selected}
            nodeType={selectedNodeType}
            onNavigate={(id) => {
              selectNode(id)
              // Expand parent nodes if navigating to an instance
              const parentClass = ALL_CLASSES.find((c) =>
                treeState.selected?.startsWith(c.uri.split('#')[0])
              )
              if (parentClass) {
                setTreeState((prev) => {
                  const newExpanded = new Set(prev.expanded)
                  newExpanded.add(parentClass.domain)
                  newExpanded.add(parentClass.uri)
                  return { ...prev, expanded: newExpanded, selected: id }
                })
              }
            }}
            onDeselect={() => setTreeState((prev) => ({ ...prev, selected: null }))}
          />
        ) : (
          <Card style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', maxWidth: '320px' }}>
              <div style={{ width: '64px', height: '64px', borderRadius: '16px', backgroundColor: 'var(--color-warm-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <Folder style={{ width: '32px', height: '32px', color: 'var(--color-warm-500)' }} />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '8px' }}>
                Select a Node
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                Expand domains and classes to browse instances. Click on any node to see its details.
              </p>
            </div>
          </Card>
        )}
      </div>

      {/* Create Instance Dialog */}
      {createForClass && (
        <CreateInstanceDialog
          classUri={createForClass.uri}
          classLabel={createForClass.localName}
          color={DOMAIN_COLORS[createForClass.domain].border}
          bgColor={DOMAIN_COLORS[createForClass.domain].bg}
          onClose={() => setCreateForClass(null)}
          onCreated={(uri) => {
            // Select the newly created instance and expand its parent class
            setTreeState((prev) => {
              const newExpanded = new Set(prev.expanded)
              newExpanded.add(createForClass.domain)
              newExpanded.add(createForClass.uri)
              return { ...prev, expanded: newExpanded, selected: uri }
            })
          }}
        />
      )}
    </div>
  )
}

interface DomainNodeProps {
  domain: OntologyDomain
  classes: OntologyClass[]
  expanded: Set<string>
  selected: string | null
  instanceCounts: Record<string, number>
  onToggle: (id: string) => void
  onSelect: (id: string) => void
  onRequestCreate: (ontologyClass: OntologyClass) => void
}

function DomainNode({ domain, classes, expanded, selected, instanceCounts, onToggle, onSelect, onRequestCreate }: DomainNodeProps) {
  const isExpanded = expanded.has(domain)
  const isSelected = selected === domain
  const colors = DOMAIN_COLORS[domain]
  const groups = getGroupsForDomain(domain)

  // Calculate total instance count from batched counts (no individual queries)
  const totalCount = useMemo(() => {
    return classes.reduce((sum, cls) => sum + (instanceCounts[cls.uri] || 0), 0)
  }, [classes, instanceCounts])

  return (
    <div>
      <div
        onClick={() => {
          onToggle(domain)
          onSelect(domain)
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 12px',
          borderRadius: '8px',
          cursor: 'pointer',
          backgroundColor: isSelected ? colors.bg : 'transparent',
          border: isSelected ? `1px solid ${colors.border}` : '1px solid transparent',
          transition: 'all 0.15s',
        }}
      >
        {isExpanded ? (
          <ChevronDown style={{ width: '16px', height: '16px', color: colors.border, flexShrink: 0 }} />
        ) : (
          <ChevronRight style={{ width: '16px', height: '16px', color: 'var(--color-text-muted)', flexShrink: 0 }} />
        )}
        {isExpanded ? (
          <FolderOpen style={{ width: '18px', height: '18px', color: colors.border, flexShrink: 0 }} />
        ) : (
          <Folder style={{ width: '18px', height: '18px', color: colors.border, flexShrink: 0 }} />
        )}
        <span style={{ flex: 1, fontWeight: 600, fontSize: '13px', color: colors.text }}>
          {DOMAIN_LABELS[domain]}
        </span>
        <Badge style={{
          backgroundColor: totalCount === 0 ? 'var(--color-warm-100)' : colors.bg,
          color: totalCount === 0 ? 'var(--color-text-muted)' : colors.text,
          fontSize: '10px',
          border: totalCount === 0 ? '1px solid var(--color-border-subtle)' : `1px solid ${colors.border}40`
        }}>
          {totalCount}
        </Badge>
      </div>

      {isExpanded && (
        <div style={{ marginLeft: '24px', borderLeft: `1px solid ${colors.border}20`, paddingLeft: '8px' }}>
          {groups.map((group) => (
            <GroupNode
              key={group.id}
              group={group}
              domain={domain}
              expanded={expanded}
              selected={selected}
              instanceCounts={instanceCounts}
              onToggle={onToggle}
              onSelect={onSelect}
              onRequestCreate={onRequestCreate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface GroupNodeProps {
  group: EntityGroup
  domain: OntologyDomain
  expanded: Set<string>
  selected: string | null
  instanceCounts: Record<string, number>
  onToggle: (id: string) => void
  onSelect: (id: string) => void
  onRequestCreate: (ontologyClass: OntologyClass) => void
}

function GroupNode({ group, domain, expanded, selected, instanceCounts, onToggle, onSelect, onRequestCreate }: GroupNodeProps) {
  const groupKey = `group-${domain}-${group.id}`
  const isExpanded = expanded.has(groupKey)
  const colors = DOMAIN_COLORS[domain]
  const groupClasses = getClassesByGroup(domain, group.id)

  // Calculate total instance count from batched counts (no individual queries)
  const groupInstanceCount = useMemo(() => {
    return groupClasses.reduce((sum, cls) => sum + (instanceCounts[cls.uri] || 0), 0)
  }, [groupClasses, instanceCounts])

  return (
    <div>
      <div
        onClick={() => onToggle(groupKey)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 8px',
          borderRadius: '6px',
          cursor: 'pointer',
          backgroundColor: 'transparent',
          transition: 'all 0.15s',
        }}
      >
        {isExpanded ? (
          <ChevronDown style={{ width: '14px', height: '14px', color: 'var(--color-text-muted)', flexShrink: 0 }} />
        ) : (
          <ChevronRight style={{ width: '14px', height: '14px', color: 'var(--color-text-muted)', flexShrink: 0 }} />
        )}
        <span style={{ flex: 1, fontSize: '12px', fontWeight: 500, color: 'var(--color-text)' }}>
          {group.label}
        </span>
        <span style={{
          fontSize: '9px',
          color: groupInstanceCount === 0 ? 'var(--color-text-muted)' : colors.text,
          backgroundColor: groupInstanceCount === 0 ? 'var(--color-warm-100)' : colors.bg,
          border: groupInstanceCount === 0 ? '1px solid var(--color-border-subtle)' : `1px solid ${colors.border}40`,
          padding: '1px 5px',
          borderRadius: '4px',
        }}>
          {groupInstanceCount}
        </span>
      </div>

      {isExpanded && (
        <div style={{ marginLeft: '16px', borderLeft: '1px solid var(--color-border-subtle)', paddingLeft: '8px' }}>
          {groupClasses.map((cls) => (
            <ClassNode
              key={cls.uri}
              ontologyClass={cls}
              expanded={expanded}
              selected={selected}
              instanceCount={instanceCounts[cls.uri] || 0}
              onToggle={onToggle}
              onSelect={onSelect}
              onRequestCreate={onRequestCreate}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface ClassNodeProps {
  ontologyClass: OntologyClass
  expanded: Set<string>
  selected: string | null
  instanceCount: number
  onToggle: (id: string) => void
  onSelect: (id: string) => void
  onRequestCreate: (ontologyClass: OntologyClass) => void
}

function ClassNode({ ontologyClass, expanded, selected, instanceCount, onToggle, onSelect, onRequestCreate }: ClassNodeProps) {
  const isExpanded = expanded.has(ontologyClass.uri)
  const isSelected = selected === ontologyClass.uri
  const colors = DOMAIN_COLORS[ontologyClass.domain]

  // Fetch instances when expanded
  const { data: instances = [], isLoading } = useQuery({
    queryKey: ['instances', ontologyClass.uri],
    queryFn: async () => {
      const result = await getInstancesOfClass(ontologyClass.uri, 100, 0)
      return result.results.bindings.map((b) => ({
        uri: b.instance?.value || '',
        // For Facets: prefer title > content > label > localName from URI
        label: b.title?.value || b.content?.value || b.label?.value || getLocalName(b.instance?.value || ''),
      }))
    },
    enabled: isExpanded,
    staleTime: 30000,
  })

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 10px',
          borderRadius: '6px',
          backgroundColor: isSelected ? 'var(--color-warm-100)' : 'transparent',
          transition: 'all 0.15s',
        }}
      >
        <div
          onClick={() => {
            onToggle(ontologyClass.uri)
            onSelect(ontologyClass.uri)
          }}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, cursor: 'pointer' }}
        >
          {instanceCount > 0 ? (
            isExpanded ? (
              <ChevronDown style={{ width: '14px', height: '14px', color: 'var(--color-text-muted)', flexShrink: 0 }} />
            ) : (
              <ChevronRight style={{ width: '14px', height: '14px', color: 'var(--color-text-muted)', flexShrink: 0 }} />
            )
          ) : (
            <span style={{ width: '14px' }} />
          )}
          <Box style={{ width: '16px', height: '16px', color: colors.border, flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: '12px', fontFamily: 'monospace', color: 'var(--color-text)' }}>
            {ontologyClass.localName}
          </span>
          <span style={{
            fontSize: '11px',
            color: instanceCount === 0 ? 'var(--color-text-muted)' : colors.text,
            backgroundColor: instanceCount === 0 ? 'var(--color-warm-100)' : colors.bg,
            border: instanceCount === 0 ? '1px solid var(--color-border-subtle)' : `1px solid ${colors.border}40`,
            padding: '2px 6px',
            borderRadius: '4px'
          }}>
            {instanceCount}
          </span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRequestCreate(ontologyClass)
          }}
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}40`,
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: 0.5,
            transition: 'opacity 0.15s',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
          title={`Add ${ontologyClass.localName}`}
        >
          <Plus style={{ width: '12px', height: '12px', color: colors.border }} />
        </button>
      </div>

      {isExpanded && instanceCount > 0 && (
        <div style={{ marginLeft: '20px', borderLeft: '1px solid var(--color-border-subtle)', paddingLeft: '8px' }}>
          {isLoading ? (
            <div style={{ padding: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text-muted)', fontSize: '12px' }}>
              <Loader2 style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }} />
              Loading instances...
            </div>
          ) : (
            instances.map((instance) => (
              <InstanceNode
                key={instance.uri}
                uri={instance.uri}
                label={instance.label}
                domain={ontologyClass.domain}
                selected={selected}
                onSelect={onSelect}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

interface InstanceNodeProps {
  uri: string
  label: string
  domain: OntologyDomain
  selected: string | null
  onSelect: (id: string) => void
}

function InstanceNode({ uri, label, domain, selected, onSelect }: InstanceNodeProps) {
  const isSelected = selected === uri
  const colors = DOMAIN_COLORS[domain]

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        onSelect(uri)
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 10px',
        borderRadius: '6px',
        cursor: 'pointer',
        backgroundColor: isSelected ? colors.bg : 'transparent',
        border: isSelected ? `1px solid ${colors.border}` : '1px solid transparent',
        transition: 'all 0.15s',
      }}
    >
      <Circle style={{ width: '8px', height: '8px', fill: colors.border, color: colors.border, flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: '12px', color: colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
    </div>
  )
}

interface DetailPanelProps {
  nodeId: string
  nodeType: 'domain' | 'class' | 'instance' | null
  onNavigate: (id: string) => void
  onDeselect: () => void
}

function DetailPanel({ nodeId, nodeType, onNavigate, onDeselect }: DetailPanelProps) {
  if (nodeType === 'domain') {
    return <DomainDetail domain={nodeId as OntologyDomain} />
  }

  if (nodeType === 'class') {
    const ontologyClass = ALL_CLASSES.find((c) => c.uri === nodeId)
    if (ontologyClass) {
      return <ClassDetailView ontologyClass={ontologyClass} onNavigate={onNavigate} />
    }
  }

  if (nodeType === 'instance') {
    return <InstanceDetailView instanceUri={nodeId} onNavigate={onNavigate} onDeselect={onDeselect} />
  }

  return null
}

function DomainDetail({ domain }: { domain: OntologyDomain }) {
  const colors = DOMAIN_COLORS[domain]
  const classes = ALL_CLASSES.filter((c) => c.domain === domain)

  const descriptions: Record<OntologyDomain, string> = {
    core: 'Core infrastructure classes including Sources and Facets that provide the foundation for knowledge extraction.',
    market: 'External market forces, industry trends, competitors, and customer segments that influence business strategy.',
    business: 'Business model components including value propositions, customer relationships, revenue streams, and strategic goals.',
    solution: 'Product features, requirements, user stories, and technical implementation details.',
  }

  return (
    <Card style={{ height: '100%', overflow: 'auto' }}>
      <CardHeader style={{ borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Folder style={{ width: '24px', height: '24px', color: colors.border }} />
          </div>
          <div>
            <CardTitle style={{ fontSize: '18px', color: colors.text }}>{DOMAIN_LABELS[domain]}</CardTitle>
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
              {classes.length} ontology classes
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent style={{ padding: '20px' }}>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: '20px' }}>
          {descriptions[domain]}
        </p>
        <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '12px' }}>
          Classes in this domain
        </h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {classes.map((cls) => (
            <Badge key={cls.uri} variant="outline" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
              {cls.localName}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function ClassDetailView({ ontologyClass, onNavigate }: { ontologyClass: OntologyClass; onNavigate: (id: string) => void }) {
  const colors = DOMAIN_COLORS[ontologyClass.domain]

  // Use hook instead of direct query (likely already cached from tree view)
  const { data: instanceCount = 0 } = useInstanceCount(ontologyClass.uri)

  return (
    <Card style={{ height: '100%', overflow: 'auto' }}>
      <CardHeader style={{ borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box style={{ width: '24px', height: '24px', color: colors.border }} />
          </div>
          <div style={{ flex: 1 }}>
            <CardTitle style={{ fontSize: '18px', fontFamily: 'monospace' }}>{ontologyClass.localName}</CardTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <Badge style={{ backgroundColor: colors.bg, color: colors.text, fontSize: '10px', border: `1px solid ${colors.border}40` }}>
                {ontologyClass.domain}
              </Badge>
              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                {instanceCount} instances
              </span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent style={{ padding: '20px' }}>
        {ontologyClass.description && (
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.6, marginBottom: '20px' }}>
            {ontologyClass.description}
          </p>
        )}

        {ontologyClass.properties.length > 0 && (
          <div>
            <h4 style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', marginBottom: '12px' }}>
              Properties ({ontologyClass.properties.length})
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {ontologyClass.properties.map((prop) => (
                <div
                  key={prop.uri}
                  style={{
                    padding: '10px 12px',
                    backgroundColor: 'var(--color-warm-50)',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 500 }}>{prop.localName}</div>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                    {prop.type === 'object' ? (
                      <button
                        onClick={() => onNavigate(prop.range)}
                        style={{ color: 'var(--color-primary)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        → {getLocalName(prop.range)}
                      </button>
                    ) : (
                      <span>{getLocalName(prop.range)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface InstanceDetailViewProps {
  instanceUri: string
  onNavigate: (id: string) => void
  onDeselect: () => void
}

function InstanceDetailView({ instanceUri, onNavigate, onDeselect }: InstanceDetailViewProps) {
  const queryClient = useQueryClient()

  // Determine color based on instance type
  const ontologyClass = ALL_CLASSES.find((c) =>
    instanceUri.includes(c.uri.split('#')[0])
  )
  const colors = ontologyClass ? DOMAIN_COLORS[ontologyClass.domain] : DOMAIN_COLORS.business

  const handleDeleted = () => {
    // Invalidate instance counts and lists
    queryClient.invalidateQueries({ queryKey: ['instanceCount'] })
    queryClient.invalidateQueries({ queryKey: ['instances'] })
    queryClient.invalidateQueries({ queryKey: ['domainInstanceCount'] })
    onDeselect()
  }

  return (
    <InstanceDetailPanel
      instanceUri={instanceUri}
      color={colors.border}
      bgColor={colors.bg}
      onClose={onDeselect}
      onNavigate={onNavigate}
      onDeleted={handleDeleted}
      fullWidth
    />
  )
}
